import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface OwnerRecord {
  owner_id: string;
  type_owner: string;
  owner_name: string;
  role_code_owner: string;
  role_text_owner: string;
  percentage_ownership: number | null;
  city_owner: string;
  state_owner: string;
  zip_code_owner: string;
  association_date_owner: string;
  owner_category?: string;
}

interface OtherFacilityOwner {
  owner_id: string;
  owner_name: string;
  percentage_ownership: number | null;
  role_text_owner: string;
}

interface OtherFacilityRecord {
  ccn: string;
  facility_name: string;
  dba_name?: string;
  state: string;
  in_understaffing_analysis: boolean;
  mds_cost: number | null;
  owners: OtherFacilityOwner[];
}

interface OwnerGroupRecord {
  group_id: string;
  owner_name: string;
  projected_understaffing: number | null;
  state: string;
}

interface OwnershipSnapshotProps {
  owners: OwnerRecord[];
  otherFacilities: OtherFacilityRecord[];
  facilityState: string;
  fromState?: string;
  ownerGroups?: OwnerGroupRecord[];
}

const fmtMoney = (v: number | null | undefined) => {
  if (v == null) return '-';
  const p = v < 0 ? '-$' : '$';
  return `${p}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function OwnershipSnapshot({ owners, otherFacilities, facilityState, fromState, ownerGroups }: OwnershipSnapshotProps) {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Owners bucketed by category */}
      {owners.length > 0 && (() => {
        const groups: Record<string, { label: string; showPct: boolean; items: typeof owners }> = {
          direct: { label: '5%+ Direct Ownership', showPct: true, items: [] },
          indirect: { label: '5%+ Indirect Ownership', showPct: true, items: [] },
          managerial: { label: 'Managerial Control', showPct: false, items: [] },
          financial: { label: 'Financial Interest', showPct: true, items: [] },
          officers: { label: 'Officers', showPct: false, items: [] },
          other: { label: 'Other', showPct: false, items: [] },
        };
        for (const o of owners) {
          const cat = o.owner_category || 'other';
          if (groups[cat]) groups[cat].items.push(o);
          else groups.other.items.push(o);
        }
        const buckets = Object.values(groups).filter(b => b.items.length > 0);
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {buckets.map(b => (
              <Box key={b.label} sx={{ flex: 1, minWidth: 140 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                  {b.label}
                </Typography>
                {b.items.map((o, i) => (
                  <Box key={`${o.owner_id}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.2 }}>
                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {o.owner_name || 'Unknown'}
                    </Typography>
                    {b.showPct && o.percentage_ownership != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {o.percentage_ownership.toFixed(0)}%
                      </Typography>
                    )}
                    {!b.showPct && (
                      <Typography variant="caption" sx={{ flexShrink: 0, color: 'text.disabled', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, fontSize: '0.55rem' }}>
                        {o.type_owner === 'O' ? 'Org' : o.type_owner === 'I' ? 'Ind' : ''}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Owner Groups table */}
      {ownerGroups && ownerGroups.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, pb: 0.75, display: 'block' }}>
            Owner Groups ({ownerGroups.length})
          </Typography>
          <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', '& td, & th': { py: 0.375, px: 0.75, fontSize: '0.75rem' } }}>
            <Box component="colgroup">
              <Box component="col" sx={{ width: '60%' }} />
              <Box component="col" sx={{ width: '40%' }} />
            </Box>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="th" sx={{ textAlign: 'left', fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem !important' }}>Owner</Box>
                <Box component="th" sx={{ textAlign: 'left', fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem !important' }}>Link</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {ownerGroups.map((og) => (
                <Box component="tr" key={og.group_id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                  <Box component="td" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {og.owner_name}
                  </Box>
                  <Box component="td">
                    <Typography
                      variant="caption"
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, whiteSpace: 'nowrap' }}
                      onClick={() => navigate(`/schemes/snf-understaffing/owner-group/${og.group_id}${og.state ? `?state=${og.state}` : ''}`)}
                    >
                      View Owner Group
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Other facilities table */}
      {otherFacilities.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, pb: 0.75, display: 'block' }}>
            Owner Group Facilities ({otherFacilities.length})
          </Typography>
          <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', '& td, & th': { py: 0.375, px: 0.75, fontSize: '0.75rem' } }}>
            <Box component="colgroup">
              <Box component="col" sx={{ width: '60%' }} />
              <Box component="col" sx={{ width: '40%' }} />
            </Box>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="th" sx={{ textAlign: 'left', fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem !important' }}>Facility</Box>
                <Box component="th" sx={{ textAlign: 'left', fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem !important' }}>Link</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {otherFacilities.map((f, i) => (
                <Box
                  component="tr"
                  key={`${f.ccn}-${i}`}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Box component="td" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {f.dba_name || f.facility_name || f.ccn}
                  </Box>
                  <Box component="td">
                    {f.in_understaffing_analysis ? (
                      <Typography
                        variant="caption"
                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, whiteSpace: 'nowrap' }}
                        onClick={() => navigate(`/schemes/snf-understaffing/facility/${f.ccn}`, { state: fromState ? { fromState } : undefined })}
                      >
                        View Facility
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">–</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
